"use client";

export enum PriceCurrency {
  Usd = "USD",
  Eur = "EUR",
  Gbp = "GBP",
  Pln = "PLN",
}

export interface PricingConfiguration {
  id: string;
  amount: number;
  currency: string;
  display_price: string;
  is_default: boolean;
  is_subject_specific: boolean;
  created_at: string;
  updated_at: string;
  subject?: {
    id: string;
    name: string;
    tutor_profile_subject_id: string;
  };
  curriculum_level?: {
    id: string;
    name: string;
  };
}

export interface PricingResponse {
  data: PricingConfiguration[];
}

export interface UpdatePricingRequest {
  amount: number;
  currency: string;
}

export interface CreateSubjectPricingRequest extends UpdatePricingRequest {
  tutor_profile_subject_id: string;
  curriculum_level_id: string;
}

// Subject with curriculum levels types
export interface CurriculumLevel {
  id: string;
  name: string;
  description: string;
}

export interface Subject {
  id: string;
  name: string;
  icon: string;
}

// Child subject structure
export interface ChildSubject {
  id: string; // tutor profile subject id
  subject: Subject;
  curriculum_levels: CurriculumLevel[];
}

// Parent subject structure (can have children)
export interface SubjectWithCurriculumLevels {
  id: string; // tutor profile subject id
  name: string;
  icon: string;
  is_group: boolean;
  is_editable: boolean;
  curriculum_levels: CurriculumLevel[];
  children?: ChildSubject[];
  // For compatibility with child subjects
  subject?: Subject;
}

export interface SubjectsWithCurriculumLevelsResponse {
  data: SubjectWithCurriculumLevels[];
}

// Helper functions for pricing
export const getCurrencySymbol = (currency: PriceCurrency): string => {
  switch (currency) {
    case PriceCurrency.Usd:
      return "$";
    case PriceCurrency.Eur:
      return "€";
    case PriceCurrency.Gbp:
      return "£";
    case PriceCurrency.Pln:
      return "zł";
    default:
      return "$";
  }
};

// Suggested price tiers based on currency
export const getPriceTiers = (currency: PriceCurrency): number[] => {
  switch (currency) {
    case PriceCurrency.Usd:
      return [15, 20, 25, 30, 35];
    case PriceCurrency.Eur:
      return [15, 20, 25, 30, 35];
    case PriceCurrency.Gbp:
      return [15, 20, 25, 30, 35];
    case PriceCurrency.Pln:
      return [60, 80, 100, 120, 140];
    default:
      return [15, 20, 25, 30, 35];
  }
};

// Render price tiers as buttons
export const renderPriceTiers = (currency: PriceCurrency, onSelect: (price: number) => void) => {
  const tiers = getPriceTiers(currency);

  return tiers.map((price) => (
    <button
      key={`price-${price}`}
      className="bg-muted hover:bg-side-accent flex h-9 items-center justify-center rounded-md px-3 text-xs font-medium transition-colors sm:h-10 sm:px-4 sm:text-sm md:text-base"
      type="button"
      onClick={() => onSelect(price)}
    >
      {getCurrencySymbol(currency)}
      {price}
    </button>
  ));
};
